import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  Link,
  Typography,
  TextField,
} from "@newulife/common";
import { login, getOidcInteraction, finishOidcInteraction } from "../api";
import {
  resolveInteractionUid,
  clearInteractionUid,
} from "../oidcInteraction";
import { useAppContext } from "../context/AppContext";
import { LoaderRing } from "../components/Loader";
import "./login.css";
import Message from "../components/Message";
import MarkdownLinksOnly from "../components/MarkdownLinksOnly";

export default function Login({ isMacau }) {
  // context
  const {
    state: {
      dictionary: { login: c_login },
    },
    actions: { setMessage },
  } = useAppContext();

  // state
  const queryParams = new URLSearchParams(window.location.search);
  const redirectQuery = queryParams.get("redirect") || "";
  const storeParam = queryParams.get("store") || "";
  const storeQuery = storeParam || "shop-us.newulife.com";
  const refererQuery = queryParams.get("referer") || "";
  // Persist interaction uid so SSO finish cannot fall through to /api/login/shopify
  // if the query string is dropped mid-session (refresh, soft nav, etc.).
  const [interaction, setInteraction] = useState(() => resolveInteractionUid());
  const [form, set_form] = useState({
    "dist-id": "",
    password: "",
    redirectUrl: redirectQuery,
  });
  const [error, setError] = useState("");
  const [loader, set_loader] = useState(true);
  const [interactionReady, setInteractionReady] = useState(!interaction);
  const guid = queryParams.get("guid") || "";
  const loggedRepGuid = queryParams.get("loggedRepGuid") || "";
  const onBehalfRepGuid = queryParams.get("onBehalfRepGuid") || "";
  const onBehalfCustomerGuid = queryParams.get("onBehalfCustomerGuid") || "";
  const referer = refererQuery || window.__INITIAL_REFERRER__ || "";

  const showError = useCallback(
    (errorMessage, type = "error") => {
      if (isMacau) {
        setError(errorMessage);
      } else {
        setMessage({ text: errorMessage, type });
      }
    },
    [isMacau, setMessage]
  );

  const handleGUIDLogin = useCallback(async () => {
    // Never mix GUID SSO with an active OIDC interaction.
    if (interaction) {
      console.warn(
        "[login] skipping GUID login because OIDC interaction is active",
        interaction
      );
      return;
    }
    const { error, url } = await login({
      isMacau,
      guid,
      onBehalfRepGuid,
      loggedRepGuid,
      onBehalfCustomerGuid,
      redirectUrl: redirectQuery,
      referer,
      store: storeParam,
    });
    if (error) {
      set_loader(false);
      showError(error, "info");
    } else if (url) {
      window.location.replace(url);
    }
  }, [
    interaction,
    guid,
    onBehalfRepGuid,
    loggedRepGuid,
    onBehalfCustomerGuid,
    redirectQuery,
    referer,
    storeParam,
    isMacau,
    showError,
  ]);

  useEffect(() => {
    if (interaction) {
      let cancelled = false;
      (async () => {
        const details = await getOidcInteraction(interaction);
        if (cancelled) return;
        if (details?.error) {
          clearInteractionUid();
          setInteraction("");
          showError(
            details.error ||
              "Session expired. Please restart login from the store."
          );
          setInteractionReady(false);
        } else {
          setInteractionReady(true);
        }
        set_loader(false);
      })();
      return () => {
        cancelled = true;
      };
    }

    if (guid || (loggedRepGuid && (onBehalfRepGuid || onBehalfCustomerGuid))) {
      handleGUIDLogin();
    } else {
      set_loader(false);
    }
  }, [
    interaction,
    guid,
    onBehalfRepGuid,
    loggedRepGuid,
    onBehalfCustomerGuid,
    handleGUIDLogin,
    showError,
  ]);

  // contentful text
  const [
    text_login,
    text_distId,
    text_forgotPassword,
    ,
    // text_failToLoginMessage,
    text_submit,
    text_reset,
    text_no_account,
    text_guest,
    text_macau_error,
    text_macau_error_title,
    text_password,
    text_continue_message,
  ] = c_login;

  const {
    item2: ContinueIntro,
    item3: ContinueLink,
    item4: ContinueEnd,
  } = text_continue_message;

  // Generate link for continue shopping content
  const ShopLink = "https://" + storeQuery + redirectQuery;

  // functions
  function handleChange(e) {
    const { id, value } = e.target;
    set_form({ ...form, [id]: value });
  }

  const emptyFields = !form["dist-id"] || !form.password;
  const submitDisabled =
    loader || emptyFields || (Boolean(interaction) && !interactionReady);

  function handleSubmit(e) {
    set_loader(true);
    e.preventDefault();
    if (!form["dist-id"] || !form.password) {
      showError("Please add your Distributor ID and Password");
      set_loader(false);
      return;
    }

    // Re-resolve in case state was stale; prefer in-memory interaction from mount.
    const uid = interaction || resolveInteractionUid();
    if (uid) {
      console.info("[login] finishing OIDC interaction", uid);
      finishOidcInteraction({
        uid,
        username: form["dist-id"],
        password: form.password,
      }).then(({ error, redirectTo }) => {
        if (error) {
          const expired =
            /interaction|session|expired|invalid_interaction|not_found/i.test(
              String(error)
            );
          if (expired) {
            clearInteractionUid();
            setInteraction("");
          }
          showError(error);
          set_loader(false);
        } else if (redirectTo) {
          clearInteractionUid();
          setInteraction("");
          window.location.replace(redirectTo);
        } else {
          showError("Unable to login please try again");
          set_loader(false);
        }
      });
      return;
    }

    console.info("[login] posting credentials to /api/login/shopify");
    login({ ...form, referer, store: storeParam, isMacau }).then(({ error, url }) => {
      if (error) {
        showError(error);
        set_loader(false);
      } else if (url) {
        window.location.replace(url);
      }
    });
  }

  // render
  // if (loader && !isMacau) return <LoaderRing active={loader} />;
  return (
    <section>
      <h1 className="text-center mb-40 login-text mt-40">{text_login}</h1>
      <form
        className="form-max"
        onKeyUp={(e) => {
          if (e.key === "Enter") handleSubmit(e);
        }}
      >
        <Stack spacing={4} alignItems="center">
          <Stack spacing={2} className="form-max">
            <TextField
              id="dist-id"
              color="secondary"
              errorText="Please fill out your Distributor ID"
              label={text_distId}
              onChange={handleChange}
              required
              value={form["dist-id"]}
              variant="filled"
            />
            <TextField
              id="password"
              color="secondary"
              errorText="Please fill out your password"
              label={text_password}
              onChange={handleChange}
              type="password"
              required
              value={form.password}
              variant="filled"
            />
          </Stack>

          <Stack spacing={2} alignItems="center">
            <Button
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitDisabled}
            >
              {loader ? <LoaderRing active variant="small" /> : text_submit}
            </Button>
            <Message />
            {error && (
              <Alert
                onClose={() => setError("")}
                message={text_macau_error}
                severity="error"
                variant="standard"
              >
                <Grid gap={0.5}>
                  <Typography variant="subhead1">
                    {text_macau_error_title}
                  </Typography>
                  <Typography variant="body2">{text_macau_error}</Typography>
                </Grid>
              </Alert>
            )}

            <Link color="primary" href="/pw/forgot" underline="hover">
              {text_forgotPassword}
            </Link>
          </Stack>
          {!interaction && (
            <Box>
              <p className="text-center">
                <MarkdownLinksOnly content={ContinueIntro} />{" "}
                {ContinueLink}{" "}
                <MarkdownLinksOnly content={ContinueEnd} />
              </p>
            </Box>
          )}
        </Stack>

        {/* <a href="/pw/forgot" className="mt-40">
          {text_forgotPassword}
        </a> */}
        {/* <a href="/pw/reset">{text_reset}?</a> */}
        {/* <div className="text-center">
          <div>{text_no_account}</div>
          <a href="https://store.newulife.com" className="mt-40">
            {text_guest}
          </a>
        </div> */}
      </form>
    </section>
  );
}
